(() => {
  /* Unisource Homepage v13 - exact Design Catalog artwork image logic */
  window.UNISOURCE_HOME_VERSION = 'v50-remove-dark-artwork-class';
  const roots = document.querySelectorAll('[data-unisource-home]');
  if (!roots.length) return;

  const SPOTLIGHT_DISPLAY_LIMIT = 10;
  const SPOTLIGHT_MINIMUM_PRODUCTS = 3;
  const SPOTLIGHT_REQUIRED_TAG = 'home_spotlight';
  const SPOTLIGHT_BADGE_PREFIX = 'spotlight_';
  const SPOTLIGHT_INCLUDE_TAG = 'home_spotlight';
  const FEATURED_DESIGN_DISPLAY_LIMIT = 8;
  const STOREFRONT_DISPLAY_SETTINGS_URL = '/apps/inksoft/storefront/display-settings';
  const DEFAULT_STOREFRONT_DISPLAY_SETTINGS = {
    homepageSpotlight: {
      showSku: false
    }
  };

  let storefrontDisplaySettings = DEFAULT_STOREFRONT_DISPLAY_SETTINGS;

  const loadStorefrontDisplaySettings = async () => {
    try {
      const payload = await fetchJson(STOREFRONT_DISPLAY_SETTINGS_URL);
      storefrontDisplaySettings =
        payload && payload.data
          ? payload.data
          : DEFAULT_STOREFRONT_DISPLAY_SETTINGS;
    } catch (error) {
      storefrontDisplaySettings = DEFAULT_STOREFRONT_DISPLAY_SETTINGS;
      console.warn('[Unisource Homepage] Could not load display settings:', error);
    }
  };


  const normalizeText = (value) => String(value || '').trim();

  const normalizeTag = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

  const toArray = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) return value;

    if (typeof value === 'string') {
      return value
        .split(/[,\|;\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  };

  const shuffleArray = (items) => {
    const copy = [...items];

    for (let index = copy.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      const temp = copy[index];
      copy[index] = copy[randomIndex];
      copy[randomIndex] = temp;
    }

    return copy;
  };

  const getItemsFromPayload = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;

    if (Array.isArray(payload.products)) return payload.products;
    if (Array.isArray(payload.designs)) return payload.designs;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.records)) return payload.records;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.productDetails)) return payload.productDetails;
    if (Array.isArray(payload.productList)) return payload.productList;

    if (payload.data && Array.isArray(payload.data.products)) return payload.data.products;
    if (payload.data && Array.isArray(payload.data.designs)) return payload.data.designs;
    if (payload.data && Array.isArray(payload.data.items)) return payload.data.items;
    if (payload.data && Array.isArray(payload.data.records)) return payload.data.records;
    if (payload.data && Array.isArray(payload.data.results)) return payload.data.results;

    if (payload.result && Array.isArray(payload.result.products)) return payload.result.products;
    if (payload.result && Array.isArray(payload.result.designs)) return payload.result.designs;
    if (payload.result && Array.isArray(payload.result.items)) return payload.result.items;
    if (payload.result && Array.isArray(payload.result.records)) return payload.result.records;
    if (payload.result && Array.isArray(payload.result.results)) return payload.result.results;

    return [];
  };

  const normalizeImageUrl = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';

    if (/^https?:\/\//i.test(text)) return text;
    if (text.startsWith('//')) return `https:${text}`;

    /*
      Design artwork URLs from InkSoft sometimes arrive as relative paths.
      Product apparel images usually arrive as absolute cdn.inksoft.com URLs.
      For design artwork paths, keep them on shop.unisourceapparel.com because
      /Unisource_Apparel/images/publishers/... is served there.
    */
    if (text.startsWith('/Unisource_Apparel/')) {
      return `https://shop.unisourceapparel.com${text}`;
    }

    if (text.startsWith('Unisource_Apparel/')) {
      return `https://shop.unisourceapparel.com/${text}`;
    }

    if (text.startsWith('/images/')) {
      return `https://shop.unisourceapparel.com/Unisource_Apparel${text}`;
    }

    if (text.startsWith('images/')) {
      return `https://shop.unisourceapparel.com/Unisource_Apparel/${text}`;
    }

    if (text.startsWith('/')) return `https://cdn.inksoft.com${text}`;

    return text;
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return response.json();
  };

  /* =========================================================
     SPOTLIGHT PRODUCTS
  ========================================================= */

  const getProductId = (product) =>
    product.id ||
    product.ID ||
    product.Id ||
    product.productId ||
    product.ProductId ||
    product.inksoftProductId ||
    product.inkSoftProductId ||
    product.InkSoftProductID ||
    product.inksoft_product_id ||
    '';

  const getProductName = (product) =>
    product.name ||
    product.Name ||
    product.title ||
    product.Title ||
    product.productName ||
    product.ProductName ||
    'Product';

  const getProductCode = (product) =>
    product.productSku ||
    product.ProductSku ||
    product.productSKU ||
    product.ProductSKU ||
    product.sku ||
    product.Sku ||
    product.SKU ||
    '';

  const getProductImage = (product) => {
    const direct =
      product.thumbnailUrl ||
      product.ThumbnailUrl ||
      product.thumbnail_url ||
      product.image ||
      product.Image ||
      product.imageUrl ||
      product.ImageUrl ||
      product.image_url ||
      product.thumbnail ||
      product.Thumbnail ||
      product.frontImage ||
      product.front_image ||
      product.primaryImage ||
      product.primary_image ||
      product.primaryImageUrl ||
      product.PrimaryImageUrl ||
      product.productImage ||
      product.ProductImage;

    if (direct) return normalizeImageUrl(direct);

    const raw = product.raw || product.Raw || {};

    const rawDirect =
      raw.thumbnailUrl ||
      raw.ThumbnailUrl ||
      raw.imageUrl ||
      raw.ImageUrl ||
      raw.ImageFilePath_Front ||
      raw.imageFilePath_Front ||
      raw.ColorwayImageFilePath_Front ||
      raw.colorwayImageFilePath_Front;

    if (rawDirect) return normalizeImageUrl(rawDirect);

    const styles = product.styles || product.Styles || raw.styles || raw.Styles;

    if (Array.isArray(styles) && styles.length) {
      const style = styles[0];

      const styleImage =
        style.thumbnailUrl ||
        style.ThumbnailUrl ||
        style.imageUrl ||
        style.ImageUrl ||
        style.ImageFilePath_Front ||
        style.imageFilePath_Front ||
        style.ColorwayImageFilePath_Front ||
        style.colorwayImageFilePath_Front;

      if (styleImage) return normalizeImageUrl(styleImage);

      const sides = style.sides || style.Sides;

      if (Array.isArray(sides) && sides.length) {
        const frontSide =
          sides.find((side) => String(side.Side || side.side || '').toLowerCase() === 'front') ||
          sides[0];

        return normalizeImageUrl(
          frontSide.ImageFilePath ||
            frontSide.imageFilePath ||
            frontSide.ThumbnailImageFilePath ||
            frontSide.thumbnailImageFilePath ||
            frontSide.imageUrl ||
            frontSide.ImageUrl ||
            frontSide.thumbnailUrl ||
            frontSide.ThumbnailUrl
        );
      }
    }

    return '';
  };

  const decodePathSegment = (value) => {
    try {
      return decodeURIComponent(String(value || ''));
    } catch {
      return String(value || '');
    }
  };

  const cleanColorName = (value) =>
    decodePathSegment(value)
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
      .trim();

  const getProductColor = (product, imageUrl) => {
    const raw = product.raw || product.Raw || {};
    const styles = product.styles || product.Styles || raw.styles || raw.Styles;

    if (Array.isArray(styles) && styles.length) {
      const styleName = styles[0].Name || styles[0].name;
      if (styleName) return cleanColorName(styleName);
    }

    const url = String(imageUrl || '');
    const match = url.match(/\/products\/[^/]+\/products\/[^/]+\/([^/]+)\/front/i);

    if (match && match[1]) {
      return cleanColorName(match[1]);
    }

    return '';
  };

  const getProductTags = (product) => {
    const raw = product.raw || product.Raw || {};

    const possible = [
      product.tags,
      product.Tags,
      product.searchTags,
      product.SearchTags,
      product.storeSearchTags,
      product.StoreSearchTags,
      product.store_search_tags,
      product.keywords,
      product.Keywords,

      raw.tags,
      raw.Tags,
      raw.tag,
      raw.Tag,
      raw.adminTags,
      raw.AdminTags,
      raw.designTags,
      raw.DesignTags,
      raw.searchTags,
      raw.SearchTags,
      raw.storeSearchTags,
      raw.StoreSearchTags,
      raw.store_search_tags,
      raw.keywords,
      raw.Keywords
    ];

    return possible.flatMap(toArray).map(normalizeTag).filter(Boolean);
  };

  const getSpotlightBadgeTag = (product) => {
    const tags = getProductTags(product);

    return (
      tags.find(
        (tag) =>
          tag.startsWith(SPOTLIGHT_BADGE_PREFIX) &&
          tag !== SPOTLIGHT_INCLUDE_TAG
      ) || ''
    );
  };

  const formatSpotlightBadgeLabel = (badgeTag) => {
    const key = normalizeTag(badgeTag).replace(/^spotlight_/, '');
    if (!key) return '';

    const knownLabels = {
      special_sale: 'Special Sale',
      closeout_sale: 'Closeout Sale',
      close_outsale: 'Closeout Sale',
      close_out_sale: 'Closeout Sale',
      new_arrival: 'New Arrival',
      limited_stock: 'Limited Stock',
      best_seller: 'Best Seller',
      staff_pick: 'Staff Pick',
      featured: 'Featured'
    };

    if (knownLabels[key]) return knownLabels[key];

    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
      .trim();
  };

  const buildProductUrl = (template, product) => {
    const id = getProductId(product);
    const slug = product.handle || product.slug || product.Slug || '';
    const sku =
      product.productSku ||
      product.ProductSku ||
      product.productSKU ||
      product.ProductSKU ||
      product.sku ||
      product.Sku ||
      product.SKU ||
      '';

    const productProvidedUrl =
      product.url ||
      product.Url ||
      product.productUrl ||
      product.ProductUrl;

    if (productProvidedUrl) {
      return String(productProvidedUrl);
    }

    return String(template || '/pages/inksoft-product?id=__PRODUCT_ID__&p=0')
      .replaceAll('__PRODUCT_ID__', encodeURIComponent(id))
      .replaceAll('__SLUG__', encodeURIComponent(slug))
      .replaceAll('__SKU__', encodeURIComponent(sku))
      .replaceAll('{id}', encodeURIComponent(id))
      .replaceAll('{productId}', encodeURIComponent(id))
      .replaceAll('{slug}', encodeURIComponent(slug))
      .replaceAll('{sku}', encodeURIComponent(sku));
  };

  const createProductCard = (product, productUrlTemplate) => {
    const productName = getProductName(product);
    const code = getProductCode(product);
    const image = getProductImage(product);
    const href = buildProductUrl(productUrlTemplate, product);
    const badgeTag = getSpotlightBadgeTag(product);
    const badgeLabel = formatSpotlightBadgeLabel(badgeTag);

    const card = document.createElement('a');
    card.className = 'unisource-home__product-card unisource-home__product-card--dynamic';
    card.href = href;
    card.setAttribute('aria-label', productName);

    if (badgeLabel) {
      card.classList.add('unisource-home__product-card--has-badge');
      card.dataset.spotlightBadge = badgeTag;

      const badge = document.createElement('span');
      badge.className = 'unisource-home__spotlight-badge';
      badge.textContent = badgeLabel;
      card.appendChild(badge);
    }

    const thumb = document.createElement('div');
    thumb.className = 'unisource-home__product-thumb';

    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = productName;
      img.loading = 'lazy';
      thumb.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      thumb.appendChild(placeholder);
    }

    const nameEl = document.createElement('strong');
    nameEl.textContent = productName;

    card.appendChild(thumb);
    card.appendChild(nameEl);

    const showSpotlightSku =
      storefrontDisplaySettings &&
      storefrontDisplaySettings.homepageSpotlight &&
      storefrontDisplaySettings.homepageSpotlight.showSku === true;

    if (code && showSpotlightSku) {
      const skuEl = document.createElement('em');
      skuEl.textContent = `Product SKU: ${code}`;
      card.appendChild(skuEl);
    }

    return card;
  };

  const getSpotlightSections = (root) => {
    const sections = [];

    if (root.matches && root.matches('[data-home-spotlight]')) {
      sections.push(root);
    }

    root.querySelectorAll('[data-home-spotlight]').forEach((section) => {
      sections.push(section);
    });

    return sections;
  };

  const loadSpotlightProducts = async (root) => {
    const sections = getSpotlightSections(root);

    for (const section of sections) {
      if (section.dataset.spotlightLoaded === 'true') continue;
      section.dataset.spotlightLoaded = 'true';

      const row = section.querySelector('[data-spotlight-row]');
      if (!row) continue;

      const status = section.querySelector('[data-spotlight-status]');

      // Spotlight is intentionally restricted to one exact normalized tag.
      // Hyphens and underscores are normalized by normalizeTag().
      const desiredTag = SPOTLIGHT_REQUIRED_TAG;
      const maxProductsSetting = Number(section.dataset.spotlightMax || SPOTLIGHT_DISPLAY_LIMIT);
      const maxProducts = Math.max(
        SPOTLIGHT_MINIMUM_PRODUCTS,
        Math.min(
          SPOTLIGHT_DISPLAY_LIMIT,
          Number.isFinite(maxProductsSetting) ? maxProductsSetting : SPOTLIGHT_DISPLAY_LIMIT
        )
      );

      const productUrlTemplate =
        section.dataset.productUrlTemplate || '/pages/inksoft-product?id=__PRODUCT_ID__&p=0';

      const configuredApiUrl = normalizeText(section.dataset.spotlightApiUrl);

      const apiCandidates = [
        configuredApiUrl,
        '/apps/inksoft/products/by-category?categoryId=ALL&skip=0&take=250'
      ].filter(Boolean);

      if (status) {
        status.hidden = false;
        status.textContent = 'Loading spotlight products…';
      }

      let taggedProducts = [];

      for (const apiUrl of apiCandidates) {
        try {
          const payload = await fetchJson(apiUrl);
          const products = getItemsFromPayload(payload);

          if (!products.length) continue;

          taggedProducts = products.filter((product) =>
            getProductTags(product).includes(desiredTag)
          );

          if (taggedProducts.length) break;
        } catch (error) {
          console.warn('[Unisource Spotlight] Could not load:', apiUrl, error);
        }
      }

      row.innerHTML = '';

      if (taggedProducts.length < SPOTLIGHT_MINIMUM_PRODUCTS) {
        section.dataset.spotlightCount = String(taggedProducts.length);

        if (status) {
          status.hidden = false;
          status.textContent =
            `Spotlight requires at least ${SPOTLIGHT_MINIMUM_PRODUCTS} products tagged home_spotlight. ` +
            `${taggedProducts.length} matching product${taggedProducts.length === 1 ? '' : 's'} found.`;
        }

        console.warn(
          '[Unisource Spotlight] Not enough products matched the required tag:',
          desiredTag,
          taggedProducts.length
        );
        continue;
      }

      const matchedProducts = taggedProducts.slice(0, maxProducts);
      section.dataset.spotlightCount = String(matchedProducts.length);

      matchedProducts.forEach((product) => {
        row.appendChild(createProductCard(product, productUrlTemplate));
      });

      if (status) status.hidden = true;
    }
  };

  /* =========================================================
     FEATURED DESIGN IDEAS
  ========================================================= */

  const getDesignId = (design) =>
    design.id ||
    design.ID ||
    design.designId ||
    design.DesignId ||
    design.DesignID ||
    design.inksoftDesignId ||
    design.InkSoftDesignID ||
    design.externalId ||
    design.ExternalId ||
    '';

  const getDesignTitle = (design) =>
    design.title ||
    design.Title ||
    design.name ||
    design.Name ||
    design.designName ||
    design.DesignName ||
    design.displayName ||
    design.DisplayName ||
    'Design Idea';

  const getDesignSubtitle = (design) => {
    const category = String(getDesignCategory(design) || '').toLowerCase();
    const title = String(getDesignTitle(design) || '').toLowerCase();

    const existingSubtitle =
      design.subtitle ||
      design.Subtitle ||
      design.description ||
      design.Description ||
      design.designDescription ||
      design.DesignDescription ||
      '';

    const existingText = String(existingSubtitle || '').trim();
    const existingTextLower = existingText.toLowerCase();

    /*
      Correct generic/fallback subtitle text on homepage cards.

      Some synced design records still carry the old fallback copy:
      "PE uniform design inspiration concept"

      This function keeps valid custom subtitles, but replaces the generic
      PE fallback with a better segment-specific line based on category/title.
    */
    const isGenericSubtitle =
      !existingText ||
      existingTextLower.includes('pe uniform design inspiration concept') ||
      existingTextLower === 'pe uniform' ||
      existingTextLower === 'pe uniforms' ||
      existingTextLower.includes('design inspiration concept');

    if (title.includes('arroyo seco') && (title.includes('grad') || title.includes('8th grade'))) {
      return 'Graduation and class apparel inspiration concept';
    }  

    if (category.includes('corporate') || category.includes('business') || title.includes('sunright')) {
      return 'Corporate apparel and business branding concept';
    }

    if (category.includes('spirit')) {
      return 'School spirit wear inspiration concept';
    }

    if (category.includes('club') || category.includes('organization')) {
      return 'Club and organization apparel inspiration concept';
    }

    if (category.includes('teacher') || category.includes('staff')) {
      return 'Teacher and staff apparel inspiration concept';
    }

    if (category.includes('athletic') || category.includes('athletics')) {
      return 'Athletic team apparel inspiration concept';
    }

    if (category.includes('event')) {
      return 'School event apparel inspiration concept';
    }

    if (category.includes('seasonal')) {
      return 'Seasonal apparel inspiration concept';
    }

    if (category.includes('pe') || category.includes('uniform')) {
      return 'PE uniform design inspiration concept';
    }

    if (isGenericSubtitle) {
      return 'Custom apparel design inspiration concept';
    }

    return existingText || 'Custom apparel design inspiration concept';
  };

  const getDesignCategory = (design) =>
    design.category ||
    design.Category ||
    design.segment ||
    design.Segment ||
    design.productGroup ||
    design.ProductGroup ||
    design.collection ||
    design.Collection ||
    design.design_category ||
    design.designCategory ||
    'Design Idea';

  const isUsableImageUrl = (value) => {
    const text = String(value || '').trim();

    if (!text) return false;

    // Reject obvious non-image values.
    if (text.length < 8) return false;
    if (text.includes(' ')) return false;
    if (text.startsWith('#')) return false;
    if (text.toLowerCase() === 'true') return false;
    if (text.toLowerCase() === 'false') return false;

    // Accept real URL patterns.
    if (/^https?:\/\//i.test(text)) return true;
    if (text.startsWith('//')) return true;
    if (text.startsWith('/')) return true;

    // Accept direct image filenames or paths.
    if (/\.(png|jpg|jpeg|webp|gif|svg)(\?.*)?$/i.test(text)) return true;

    return false;
  };

  const pickImageUrl = (...values) => {
    for (const value of values) {
      if (isUsableImageUrl(value)) {
        return normalizeImageUrl(value);
      }
    }

    return '';
  };

  const getFirstCanvas = (design) => {
    if (Array.isArray(design?.canvases) && design.canvases.length) {
      return design.canvases[0];
    }

    if (Array.isArray(design?.Canvases) && design.Canvases.length) {
      return design.Canvases[0];
    }

    return null;
  };

  const extractDesignInksoftId = (design) => {
    if (design?.inksoftDesignId) return String(design.inksoftDesignId);
    if (design?.InkSoftDesignID) return String(design.InkSoftDesignID);
    if (design?.DesignID) return String(design.DesignID);
    if (design?.DesignId) return String(design.DesignId);

    const firstCanvas = getFirstCanvas(design);

    const sources = [
      design?.id,
      design?.ID,
      design?.slug,
      design?.Slug,
      design?.thumbnailUrl,
      design?.ThumbnailUrl,
      design?.previewImageUrl,
      design?.PreviewImageUrl,
      design?.imageUrl,
      design?.ImageUrl,
      firstCanvas?.thumbnailUrl,
      firstCanvas?.ThumbnailUrl,
      firstCanvas?.imageUrl,
      firstCanvas?.ImageUrl,
      firstCanvas?.svgUrl,
      firstCanvas?.SvgUrl
    ];

    const patterns = [
      /DI-(\d+)/i,
      /designs\/(\d+)\./i,
      /2959-(\d+)/i,
      /-(\d{6,})$/i
    ];

    for (const source of sources) {
      const value = String(source || '');

      for (const pattern of patterns) {
        const match = value.match(pattern);
        if (match && match[1]) return match[1];
      }
    }

    return '';
  };

  const normalizeDesignCatalogImageUrl = (url) => {
    if (!url) return '';

    const raw = String(url).trim();

    if (!raw) return '';

    /*
      v25 source fix:
      Design Catalog uses the clean InkSoft publisher CDN path:
      https://cdn.inksoft.com/images/publishers/2959/designs/{id}.front.480.png

      Homepage must not use the storefront JITImage / FFFFFF preview as the
      primary source. Any publisher artwork path is normalized to cdn.inksoft.com.
      JITImage URLs are left as-is only so they can remain emergency fallbacks.
    */

    if (/^https?:\/\/cdn\.inksoft\.com\/images\/publishers\//i.test(raw)) {
      return raw;
    }

    const publisherPathMatch = raw.match(/(?:https?:\/\/shop\.unisourceapparel\.com)?\/?(?:Unisource_Apparel\/)?(images\/publishers\/.*)$/i);

    if (publisherPathMatch && publisherPathMatch[1]) {
      return `https://cdn.inksoft.com/${publisherPathMatch[1].replace(/^\/+/, '')}`;
    }

    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    if (raw.indexOf('/Unisource_Apparel/') === 0) {
      return `https://shop.unisourceapparel.com${raw}`;
    }

    if (raw.indexOf('/images/') === 0) {
      return `https://cdn.inksoft.com${raw}`;
    }

    if (raw.indexOf('Unisource_Apparel/') === 0) {
      return `https://shop.unisourceapparel.com/${raw}`;
    }

    return `https://shop.unisourceapparel.com/${raw.replace(/^\/+/, '')}`;
  };

  const buildPublisherArtworkUrl = (design) => {
    const designId = extractDesignInksoftId(design);

    if (!designId) return '';

    return `https://cdn.inksoft.com/images/publishers/2959/designs/${designId}.front.480.png`;
  };

  const isMattedInksoftImageUrl = (url) => {
    const text = String(url || '').trim();

    if (!text) return false;

    /*
      JITImage URLs with /FFFFFF/ are InkSoft-rendered previews on a white
      background. They are useful as emergency fallback only, but should not
      be used for homepage Featured Design Ideas when we want the catalog-style
      transparent artwork look.
    */
    return /\/JITImage\//i.test(text) || /\/FFFFFF\//i.test(text);
  };

  const normalizeCandidateList = (candidates) => {
    return Array.from(
      new Set(
        candidates
          .map(normalizeDesignCatalogImageUrl)
          .filter(Boolean)
          .filter(isUsableImageUrl)
      )
    );
  };

  const getDesignImageCandidates = (design) => {
    const firstCanvas = getFirstCanvas(design);

    /*
      v25:
      Homepage Featured Design Ideas should match Design Catalog source URLs.
      Preferred sources intentionally reject InkSoft JITImage / FFFFFF matte
      URLs. JIT/FFFFFF thumbnails are kept only as final emergency fallback.

      Preferred order:
      1. canvas SVG
      2. CDN publisher PNG built from InkSoft Design ID
      3. canvas PNG normalized to cdn.inksoft.com when it is a publisher path
      4. preview / transparent artwork aliases normalized to cdn.inksoft.com
      5. non-JIT generic image fields
      6. emergency JIT/FFFFFF thumbnail fallback only if all else fails
    */

    const preferredCandidates = normalizeCandidateList([
      firstCanvas?.svgUrl,
      firstCanvas?.SvgUrl,

      buildPublisherArtworkUrl(design),

      firstCanvas?.imageUrl,
      firstCanvas?.ImageUrl,

      design.transparentArtworkUrl,
      design.TransparentArtworkUrl,
      design.previewImageUrl,
      design.PreviewImageUrl,
      design.previewUrl,
      design.PreviewUrl,
      design.cardImageUrl,
      design.CardImageUrl,
      design.displayImageUrl,
      design.DisplayImageUrl,
      design.frontMockupUrl,
      design.FrontMockupUrl,
      design.front_mockup_url,
      design.mockupUrl,
      design.MockupUrl,
      design.heroImageUrl,
      design.HeroImageUrl,

      design.imageUrl,
      design.ImageUrl
    ]).filter((url) => !isMattedInksoftImageUrl(url));

    const emergencyCandidates = normalizeCandidateList([
      firstCanvas?.thumbnailUrl,
      firstCanvas?.ThumbnailUrl,
      design.thumbnailUrl,
      design.ThumbnailUrl
    ]);

    const emergencyNonDuplicates = emergencyCandidates.filter(
      (url) => !preferredCandidates.includes(url)
    );

    return [...preferredCandidates, ...emergencyNonDuplicates];
  };

  const getDesignImage = (design) => getDesignImageCandidates(design)[0] || '';

  const advanceDesignImageFallback = (img) => {
    let fallbackImages = [];

    try {
      fallbackImages = JSON.parse(img.dataset.fallbackImages || '[]');
    } catch (_error) {
      fallbackImages = [];
    }

    let index = Number(img.dataset.fallbackIndex || '1');

    while (index < fallbackImages.length) {
      const nextSrc = fallbackImages[index];
      index += 1;
      img.dataset.fallbackIndex = String(index);

      if (nextSrc && nextSrc !== img.src) {
        img.src = nextSrc;
        return;
      }
    }

    img.style.display = 'none';

    const thumb = img.closest('.uh-home-artwork-wrap');

    if (thumb && !thumb.querySelector('.unisource-home__design-image-fallback')) {
      const fallback = document.createElement('span');
      fallback.className = 'uh-home-artwork-fallback';
      fallback.textContent = img.alt || 'Design preview';
      thumb.appendChild(fallback);
    }
  };

  const getDesignTags = (design) => {
    const raw = design.raw || design.Raw || {};
    const meta = design.meta || design.Meta || {};
    const fields = design.fields || design.Fields || {};
    const data = design.data || design.Data || {};

    const possible = [
      design.tags,
      design.Tags,
      design.tag,
      design.Tag,
      design.adminTags,
      design.AdminTags,
      design.designTags,
      design.DesignTags,
      design.searchTags,
      design.SearchTags,
      design.storeSearchTags,
      design.StoreSearchTags,
      design.keywords,
      design.Keywords,
      design.keyword,
      design.Keyword,
      design.collections,
      design.Collections,
      design.collection,
      design.Collection,
      design.category,
      design.Category,
      design.segment,
      design.Segment,
      design.theme,
      design.Theme,
      design.homeTag,
      design.HomeTag,
      design.featuredTag,
      design.FeaturedTag,
      design.home_featured_design,

      raw.tags,
      raw.Tags,
      raw.tag,
      raw.Tag,
      raw.adminTags,
      raw.AdminTags,
      raw.designTags,
      raw.DesignTags,
      raw.searchTags,
      raw.SearchTags,
      raw.storeSearchTags,
      raw.StoreSearchTags,
      raw.keywords,
      raw.Keywords,
      raw.keyword,
      raw.Keyword,
      raw.collections,
      raw.Collections,
      raw.collection,
      raw.Collection,
      raw.category,
      raw.Category,
      raw.segment,
      raw.Segment,
      raw.theme,
      raw.Theme,
      raw.homeTag,
      raw.HomeTag,
      raw.featuredTag,
      raw.FeaturedTag,
      raw.home_featured_design,

      meta.tags,
      meta.Tags,
      meta.tag,
      meta.Tag,
      meta.adminTags,
      meta.AdminTags,
      meta.designTags,
      meta.DesignTags,
      meta.keywords,
      meta.Keywords,
      meta.collection,
      meta.Collection,
      meta.category,
      meta.Category,
      meta.segment,
      meta.Segment,
      meta.theme,
      meta.Theme,
      meta.homeTag,
      meta.HomeTag,
      meta.featuredTag,
      meta.FeaturedTag,
      meta.home_featured_design,

      fields.tags,
      fields.Tags,
      fields.tag,
      fields.Tag,
      fields.adminTags,
      fields.AdminTags,
      fields.designTags,
      fields.DesignTags,
      fields.keywords,
      fields.Keywords,
      fields.collection,
      fields.Collection,
      fields.category,
      fields.Category,
      fields.segment,
      fields.Segment,
      fields.theme,
      fields.Theme,
      fields.homeTag,
      fields.HomeTag,
      fields.featuredTag,
      fields.FeaturedTag,
      fields.home_featured_design,

      data.tags,
      data.Tags,
      data.tag,
      data.Tag,
      data.adminTags,
      data.AdminTags,
      data.designTags,
      data.DesignTags,
      data.keywords,
      data.Keywords,
      data.collection,
      data.Collection,
      data.category,
      data.Category,
      data.segment,
      data.Segment,
      data.theme,
      data.Theme,
      data.homeTag,
      data.HomeTag,
      data.featuredTag,
      data.FeaturedTag,
      data.home_featured_design
    ];

    return possible.flatMap(toArray).map(normalizeTag).filter(Boolean);
  };


  /*
    Temporary frontend override list.

    Why this exists:
    InkSoft admin notes/tags such as dark_bg are not always included in the
    /design-inspiration/designs API cache yet. Until the backend transform/sync
    passes dark_bg into record.tags/adminTags, this list lets the homepage apply
    the dark artwork panel by title, slug, or InkSoft Design ID.

    Add more exact titles, slugs, or design IDs here as needed.
  */
  /*
    Strict mode:
    No hardcoded dark background titles/IDs.

    Dark background now applies only when the API record contains one of the
    approved dark artwork tags/flags, such as dark_bg or needsDarkBackground.
  */
  const DEFAULT_DARK_ARTWORK_OVERRIDES = [];

  const getManualDarkArtworkKeys = () => {
    const fromWindow = []
      .concat(toArray(window.UNISOURCE_HOME_DARK_ARTWORK_IDS))
      .concat(toArray(window.UNISOURCE_DARK_ARTWORK_IDS));

    return DEFAULT_DARK_ARTWORK_OVERRIDES
      .concat(fromWindow)
      .map(normalizeTag)
      .filter(Boolean);
  };

  const designMatchesManualDarkArtworkKey = (design) => {
    const manualKeys = getManualDarkArtworkKeys();

    if (!manualKeys.length) return false;

    const candidates = [
      design.id,
      design.Id,
      design.inksoftDesignId,
      design.InksoftDesignId,
      design.InkSoftDesignId,
      design.designId,
      design.DesignId,
      design.slug,
      design.Slug,
      design.title,
      design.Title
    ].map(normalizeTag).filter(Boolean);

    /*
      Match exact IDs and also title/slug includes.
      This lets an override like "Emerson Middle School Club" match API titles
      such as "Emerson Middle School - Club" or similar punctuation variants.
    */
    return candidates.some((candidate) => {
      return manualKeys.some((key) => {
        if (!key || !candidate) return false;

        /*
          Keep this targeted. Do not let a short candidate make every card dark.
          Exact ID/slug/title matches are safest. Title/slug can include longer keys.
        */
        if (candidate === key) return true;
        if (key.length >= 8 && candidate.includes(key)) return true;
        if (candidate.length >= 8 && key.includes(candidate)) return true;

        return false;
      });
    });
  };

  const designJsonHasDarkArtworkMarker = (design) => {
    let text = '';

    try {
      text = JSON.stringify(design || {}).toLowerCase();
    } catch (_error) {
      text = '';
    }

    if (!text) return false;

    return /(^|[^a-z0-9])(needs[_\-\s]?dark[_\-\s]?bg|needs[_\-\s]?dark[_\-\s]?background|dark[_\-\s]?bg|dark[_\-\s]?background|dark[_\-\s]?charcoal[_\-\s]?bg|charcoal[_\-\s]?bg|charcoal[_\-\s]?background|light[_\-\s]?artwork|light[_\-\s]?colored[_\-\s]?artwork|white[_\-\s]?artwork|white[_\-\s]?design)([^a-z0-9]|$)/i.test(text);
  };

  const needsDarkArtworkBackground = (design) => {
    const tags = getDesignTags(design);

    const darkBgTags = new Set([
      'needs_dark_bg',
      'needs_dark_background',
      'dark_bg',
      'dark_background',
      'dark_charcoal_bg',
      'dark_charcoal_background',
      'charcoal_bg',
      'charcoal_background',
      'light_artwork',
      'light_colored_artwork',
      'white_artwork',
      'white_design'
    ]);

    const explicitFlag =
      design.needsDarkBackground === true ||
      design.needsDarkArtworkBackground === true ||
      design.darkArtwork === true ||
      design.dark_bg === true ||
      design.darkBackground === true;

    return (
      explicitFlag ||
      tags.some((tag) => darkBgTags.has(tag)) ||
      designMatchesManualDarkArtworkKey(design)
    );
  };

  const getArtworkBackgroundColor = (design) => {
    if (!design || typeof design !== 'object') return '';

    const containers = [
      design,
      design.raw,
      design.Raw,
      design.meta,
      design.Meta,
      design.fields,
      design.Fields,
      design.data,
      design.Data,
      design.storefront,
      design.Storefront,
      design.presentation,
      design.Presentation
    ].filter(Boolean);

    const keys = [
      'artworkBackground',
      'ArtworkBackground',
      'artworkBackgroundColor',
      'ArtworkBackgroundColor',
      'artwork_background',
      'artwork_background_color',
      'backgroundColor',
      'BackgroundColor',
      'background_color',
      'designBackground',
      'designBackgroundColor',
      'cardBackground',
      'thumbnailBackground'
    ];

    for (const container of containers) {
      for (const key of keys) {
        if (!Object.prototype.hasOwnProperty.call(container, key)) continue;

        const value = String(container[key] == null ? '' : container[key]).trim();

        if (!value) continue;

        /*
          Accept browser-supported CSS colors while rejecting values such as
          "dark", "true", IDs, or unrelated text.
        */
        if (
          typeof CSS !== 'undefined' &&
          CSS.supports &&
          CSS.supports('background-color', value)
        ) {
          return value;
        }

        if (
          /^#[0-9a-f]{3,8}$/i.test(value) ||
          /^rgba?\(/i.test(value) ||
          /^hsla?\(/i.test(value)
        ) {
          return value;
        }
      }
    }

    return '';
  };

  const isDarkCssColor = (value) => {
    const color = String(value || '').trim();
    const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

    if (!hex) return false;

    let raw = hex[1];

    if (raw.length === 3) {
      raw = raw
        .split('')
        .map((character) => character + character)
        .join('');
    }

    const red = parseInt(raw.slice(0, 2), 16);
    const green = parseInt(raw.slice(2, 4), 16);
    const blue = parseInt(raw.slice(4, 6), 16);
    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

    return luminance < 0.48;
  };

  const isDesignFeatured = (design) => {
    const raw = design.raw || design.Raw || {};
    const meta = design.meta || design.Meta || {};
    const fields = design.fields || design.Fields || {};
    const data = design.data || design.Data || {};

    const values = [
      design.featured,
      design.Featured,
      design.isFeatured,
      design.IsFeatured,
      design.is_featured,
      design.homeFeatured,
      design.home_featured,

      raw.featured,
      raw.Featured,
      raw.isFeatured,
      raw.IsFeatured,
      raw.is_featured,
      raw.homeFeatured,
      raw.home_featured,

      meta.featured,
      meta.Featured,
      meta.isFeatured,
      meta.IsFeatured,
      meta.is_featured,
      meta.homeFeatured,
      meta.home_featured,

      fields.featured,
      fields.Featured,
      fields.isFeatured,
      fields.IsFeatured,
      fields.is_featured,
      fields.homeFeatured,
      fields.home_featured,

      data.featured,
      data.Featured,
      data.isFeatured,
      data.IsFeatured,
      data.is_featured,
      data.homeFeatured,
      data.home_featured
    ];

    return values.some((value) => {
      if (value === true) return true;
      if (value === 1) return true;

      const text = String(value || '').trim().toLowerCase();
      return text === 'true' || text === 'yes' || text === '1' || text === 'featured';
    });
  };

  const buildDesignUrl = (baseUrl, design) => {
    const id = getDesignId(design);
    const url = design.url || design.Url || design.designUrl || design.DesignUrl;

    if (url) return String(url);

    const cleanBaseUrl = normalizeText(baseUrl) || '/pages/design-inspiration-center';

    if (!id) return cleanBaseUrl;

    const separator = cleanBaseUrl.includes('?') ? '&' : '?';
    return `${cleanBaseUrl}${separator}designId=${encodeURIComponent(id)}`;
  };

  const createDesignCard = (design, baseUrl) => {
    const title = getDesignTitle(design);
    const subtitle = getDesignSubtitle(design);
    const category = getDesignCategory(design);
    const image = getDesignImage(design);
    const href = buildDesignUrl(baseUrl, design);
    const configuredArtworkBackground = getArtworkBackgroundColor(design);
    const darkArtworkBackground =
      configuredArtworkBackground
        ? isDarkCssColor(configuredArtworkBackground)
        : needsDarkArtworkBackground(design);

    const article = document.createElement('article');
    article.className = darkArtworkBackground
      ? 'unisource-home__design-card unisource-home__design-card--dynamic unisource-home__design-card--dark-artwork'
      : 'unisource-home__design-card unisource-home__design-card--dynamic';

    if (configuredArtworkBackground) {
      article.dataset.artworkBackground = configuredArtworkBackground;
    } else if (darkArtworkBackground) {
      article.dataset.artworkBackground = 'dark';
    }

    article.dataset.designTags = getDesignTags(design).join(' ');
    article.dataset.darkArtwork = darkArtworkBackground ? 'true' : 'false';

    /*
      Use a <figure> with unique classes so legacy homepage CSS like
      .unisource-home__design-thumb::before and
      .unisource-home__design-card > div:first-child cannot create an inner
      framed thumbnail box. This mirrors the Design Catalog image structure
      without reusing the old homepage thumbnail wrapper.
    */
    const thumb = document.createElement('figure');
    thumb.className = 'uh-home-artwork-wrap';

    if (configuredArtworkBackground) {
      thumb.dataset.artworkBackground = configuredArtworkBackground;

      /*
        Inline important ensures the Admin-selected value overrides older
        homepage dark-background CSS and legacy manual-ID rules.
      */
      thumb.style.setProperty(
        'background-color',
        configuredArtworkBackground,
        'important'
      );
    } else if (darkArtworkBackground) {
      thumb.dataset.artworkBackground = 'dark';
    }

    if (image) {
      const img = document.createElement('img');
      const imageCandidates = getDesignImageCandidates(design);

      img.alt = title;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.dataset.fallbackImages = JSON.stringify(imageCandidates);
      img.dataset.fallbackIndex = '1';

      /* Attach the fallback handler before assigning src. */
      img.addEventListener('error', () => advanceDesignImageFallback(img));
      img.src = image;

      thumb.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.textContent = title;
      thumb.appendChild(placeholder);
    }

    const body = document.createElement('div');

    const categoryEl = document.createElement('p');
    categoryEl.textContent = category;

    const titleEl = document.createElement('h3');
    titleEl.textContent = title;

    const linkEl = document.createElement('a');
    linkEl.href = href;
    linkEl.textContent = 'Visualize';

    body.appendChild(categoryEl);
    body.appendChild(titleEl);

    if (subtitle) {
      const subtitleEl = document.createElement('small');
      subtitleEl.textContent = subtitle;
      body.appendChild(subtitleEl);
    }

    body.appendChild(linkEl);

    article.appendChild(thumb);
    article.appendChild(body);

    return article;
  };

  const getFeaturedDesignSections = (root) => {
    const sections = [];

    if (root.matches && root.matches('[data-home-featured-designs]')) {
      sections.push(root);
    }

    root.querySelectorAll('[data-home-featured-designs]').forEach((section) => {
      sections.push(section);
    });

    return sections;
  };

  const renderFeaturedDesigns = (section) => {
    const row = section.querySelector('[data-featured-design-row]');
    if (!row) return;

    const designs = section.__unisourceFeaturedDesigns || [];
    const baseUrl = section.dataset.featuredDesignUrl || '/pages/design-inspiration-center';
    const maxSetting = Number(section.dataset.featuredDesignMax || FEATURED_DESIGN_DISPLAY_LIMIT);

    const maxDesigns = Math.min(
      FEATURED_DESIGN_DISPLAY_LIMIT,
      Number.isFinite(maxSetting) ? maxSetting : FEATURED_DESIGN_DISPLAY_LIMIT
    );

    const selectedDesigns = shuffleArray(designs).slice(0, maxDesigns);

    if (!selectedDesigns.length) return;

    row.innerHTML = '';

    selectedDesigns.forEach((design) => {
      row.appendChild(createDesignCard(design, baseUrl));
    });
  };

  const loadFeaturedDesigns = async (root) => {
    const sections = getFeaturedDesignSections(root);

    for (const section of sections) {
      if (section.dataset.featuredDesignLoaded === 'true') continue;
      section.dataset.featuredDesignLoaded = 'true';

      const row = section.querySelector('[data-featured-design-row]');
      if (!row) continue;

      const status = section.querySelector('[data-featured-design-status]');
      const refreshButton = section.querySelector('[data-featured-design-refresh]');
      const desiredTag = normalizeTag(
        section.dataset.featuredDesignTag || 'home_featured_design'
      );

      /*
        The public /search endpoint is the publication gate. It returns only
        artwork that is Public, Published, and not Archived.

        Homepage behavior:
        1. Prioritize published artwork carrying the configured featured tag.
        2. Fill remaining slots with other published artwork.
        3. Never call the broad /designs endpoint.
        4. Never display draft, internal, needs-setup, or archived records.
      */
      const apiUrl =
        '/apps/inksoft/design-inspiration/search?take=250&_home_public_v=45';

      row.innerHTML = '';

      if (status) {
        status.hidden = false;
        status.textContent = 'Loading published design ideas…';
      }

      let publishedDesigns = [];

      try {
        const payload = await fetchJson(apiUrl);
        const designs = getItemsFromPayload(payload);

        const preferred = designs.filter((design) => {
          const tags = getDesignTags(design);

          return (
            tags.includes(desiredTag) ||
            tags.includes('home_featured_design') ||
            isDesignFeatured(design)
          );
        });

        const preferredIds = new Set(
          preferred.map((design) => String(getDesignId(design)))
        );

        const remaining = designs.filter(
          (design) => !preferredIds.has(String(getDesignId(design)))
        );

        publishedDesigns = [...preferred, ...remaining];

        if (window.UNISOURCE_HOME_DEBUG === true) {
          console.log('[Unisource Featured Designs] Source:', apiUrl);
          console.log('[Unisource Featured Designs] Published records:', designs.length);
          console.log('[Unisource Featured Designs] Preferred featured records:', preferred.length);
          console.log('[Unisource Featured Designs] Published fallback records:', remaining.length);
          console.table(
            publishedDesigns.map((design, index) => ({
              order: index + 1,
              id: getDesignId(design),
              title: getDesignTitle(design),
              preferred: preferredIds.has(String(getDesignId(design))),
              tags: getDesignTags(design).join(' ')
            }))
          );
        }
      } catch (error) {
        console.warn(
          '[Unisource Featured Designs] Published artwork search failed:',
          apiUrl,
          error
        );
      }

      if (!publishedDesigns.length) {
        section.__unisourceFeaturedDesigns = [];
        row.innerHTML = '';

        if (status) {
          status.hidden = false;
          status.textContent =
            'No published public design ideas are available right now.';
        }

        if (refreshButton) refreshButton.hidden = true;
        continue;
      }

      section.__unisourceFeaturedDesigns = publishedDesigns;
      renderFeaturedDesigns(section);

      if (refreshButton) {
        refreshButton.hidden = false;

        if (refreshButton.dataset.refreshInitialized !== 'true') {
          refreshButton.dataset.refreshInitialized = 'true';
          refreshButton.addEventListener('click', () => {
            renderFeaturedDesigns(section);
          });
        }
      }

      if (status) status.hidden = true;
    }
  };

  /* =========================================================
     SHARED CAROUSEL
  ========================================================= */

  const initCarousel = (root) => {
    const buttons = Array.from(root.querySelectorAll('[data-home-scroll]'));

    buttons.forEach((button) => {
      if (button.dataset.scrollInitialized === 'true') return;
      button.dataset.scrollInitialized = 'true';

      button.addEventListener('click', () => {
        const targetName = button.dataset.target;
        const direction = button.dataset.direction === 'prev' ? -1 : 1;
        const row = root.querySelector(`[data-home-scroll-row="${targetName}"]`);
        if (!row) return;

        const scrollAmount = Math.max(260, row.clientWidth * 0.75);
        row.scrollBy({ left: scrollAmount * direction, behavior: 'smooth' });
      });
    });
  };



  /* =========================================================
     AFFILIATES CAROUSEL
  ========================================================= */
  const initAffiliatesCarousel = (root) => {
    const section = root.querySelector('[data-home-affiliates]');
    if (!section || section.dataset.affiliatesInitialized === 'true') return;

    const viewport = section.querySelector('[data-affiliate-viewport]');
    const track = section.querySelector('[data-affiliate-track]');
    const previous = section.querySelector('[data-affiliate-prev]');
    const next = section.querySelector('[data-affiliate-next]');

    if (!viewport || !track) return;

    const originalCards = Array.from(track.querySelectorAll('[data-affiliate-card]'));

    if (!originalCards.length) {
      section.hidden = true;
      return;
    }

    section.dataset.affiliatesInitialized = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const speedSeconds = Math.max(15, Number(section.dataset.affiliatesSpeed || 34));

    /*
      Duplicate the visible sequence once to create a seamless continuous loop.
      Clones are hidden from assistive technology and cannot receive focus.
    */
    originalCards.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.affiliateClone = 'true';

      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      track.appendChild(clone);
    });

    track.style.setProperty('--affiliate-duration', `${speedSeconds}s`);

    if (!reducedMotion && originalCards.length > 1) {
      track.classList.add('is-animated');
    }

    const setPaused = (paused) => {
      track.classList.toggle('is-paused', paused);
    };

    viewport.addEventListener('mouseenter', () => setPaused(true));
    viewport.addEventListener('mouseleave', () => setPaused(false));
    viewport.addEventListener('focusin', () => setPaused(true));
    viewport.addEventListener('focusout', (event) => {
      if (!viewport.contains(event.relatedTarget)) setPaused(false);
    });

    const manualScroll = (direction) => {
      setPaused(true);

      const card = viewport.querySelector('[data-affiliate-card]');
      const amount = card
        ? card.getBoundingClientRect().width + 20
        : Math.max(240, viewport.clientWidth * 0.65);

      viewport.scrollBy({
        left: direction * amount,
        behavior: 'smooth'
      });

      window.setTimeout(() => setPaused(false), 1600);
    };

    if (previous) {
      previous.addEventListener('click', () => manualScroll(-1));
    }

    if (next) {
      next.addEventListener('click', () => manualScroll(1));
    }

    /*
      Touch and wheel interaction pauses motion so users can inspect logos.
    */
    ['touchstart', 'pointerdown', 'wheel'].forEach((eventName) => {
      viewport.addEventListener(
        eventName,
        () => {
          setPaused(true);
          window.clearTimeout(viewport.__affiliateResumeTimer);
          viewport.__affiliateResumeTimer = window.setTimeout(
            () => setPaused(false),
            2200
          );
        },
        { passive: true }
      );
    });
  };

  window.UNISOURCE_HOME_DEBUG_DARK_BG = function () {
    const cards = Array.from(document.querySelectorAll('.unisource-home-section--designs .unisource-home__design-card'));

    const rows = cards.map((card, index) => {
      const figure = card.querySelector('.uh-home-artwork-wrap');
      const title = card.querySelector('h3')?.textContent?.trim() || '';
      const img = figure?.querySelector('img');

      return {
        index: index + 1,
        title,
        darkArtwork: card.dataset.darkArtwork || '',
        cardBackground: card.dataset.artworkBackground || '',
        figureClass: figure?.className || '',
        figureBackground: figure ? window.getComputedStyle(figure).backgroundColor : '',
        image: img?.currentSrc || img?.src || '',
        tags: card.dataset.designTags || ''
      };
    });

    console.table(rows);
    return rows;
  };

  const initializeHomepage = async () => {
    await loadStorefrontDisplaySettings();

    roots.forEach((root) => {
      if (root.dataset.homeInitialized === 'true') return;
      root.dataset.homeInitialized = 'true';

      initCarousel(root);
      initAffiliatesCarousel(root);
      loadSpotlightProducts(root);
      loadFeaturedDesigns(root);
    });
  };

  initializeHomepage();
})();